import { AreaService, MarketPriceService } from '@dobrozem/sdk';
import { useMutation, useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import Link from 'next/link';
import { useState } from 'react';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Loader } from '@/components/Loader';
import { Select } from '@/components/Select';
import { Text } from '@/components/Text';
import { TooltipComponent } from '@/components/Tooltip';
import { useUser } from '@/contexts/AuthProvider';
import { queryKeys } from '@/lib/react-query';
import { clientSdk } from '@/lib/sdk';
import { beautifyNumber, formatNumberValue, loadingDataSelect } from '@/utils/common';
import styles from './Calculator.module.scss';
type State = {
  area: null | string;
  district: null | string;
  region: null | string;
};
interface RegionsData {
  regions?: string[];
}
interface DistrictsData {
  districts?: string[];
}
export const Calculator = ({
  setOpenCalculator
}: {
  setOpenCalculator: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const [state, setState] = useState<State>({
    area: null,
    district: null,
    region: null
  });
  const user = useUser();
  const isAuth = !!user;
  const regions = useQuery({
    queryFn: () => AreaService.getRegionsForMarketPrice({
      client: clientSdk
    }),
    queryKey: [queryKeys.regions]
  });
  const districts = useQuery({
    enabled() {
      return !!state.region;
    },
    queryFn: () => AreaService.getDistrictsForMarketPrice({
      client: clientSdk,
      query: {
        region: state.region!
      }
    }),
    queryKey: [queryKeys.districts, state.region]
  });
  const calculateResult = useMutation({
    mutationFn: () => MarketPriceService.getMarketPrice({
      client: clientSdk,
      query: {
        area: Number(state.area!),
        district: state.district!,
        region: state.region!
      }
    }),
    mutationKey: [queryKeys.calculator, state]
  });
  const handleCalculation = () => {
    if (!state.region || !state.area || !state.district) return;
    calculateResult.mutate();
  };
  const allFieldsFilled = Object.values(state).every(value => value !== null);
  const errorMessage = calculateResult.data?.error as {
    message: string[];
  };
  return <div className={clsx(styles['content-wrapper'], calculateResult.data?.data && styles.calculated)} data-sentry-component="Calculator" data-sentry-source-file="Calculator.tsx">
      <div className={styles['btn-close']} onClick={() => setOpenCalculator(false)}>
        <svg fill='none' height='25' viewBox='0 0 25 25' width='25' xmlns='http://www.w3.org/2000/svg' data-sentry-element="svg" data-sentry-source-file="Calculator.tsx">
          <path clipRule='evenodd' d='M13.9154 12.5002L20.2075 6.2081C20.5984 5.81752 20.5984 5.18377 20.2075 4.79319C19.8166 4.40227 19.1835 4.40227 18.7926 4.79319L12.5005 11.0853L6.2081 4.79319C5.81718 4.40227 5.18411 4.40227 4.79319 4.79319C4.40227 5.18377 4.40227 5.81752 4.79319 6.2081L11.0856 12.5002L4.79319 18.7922C4.40227 19.1828 4.40227 19.8166 4.79319 20.2071C4.98865 20.4023 5.24481 20.5 5.50065 20.5C5.75648 20.5 6.01264 20.4023 6.2081 20.2068L12.5005 13.9147L18.7926 20.2068C18.988 20.4023 19.2442 20.5 19.5 20.5C19.7559 20.5 20.012 20.4023 20.2075 20.2068C20.5984 19.8162 20.5984 19.1825 20.2075 18.7919L13.9154 12.5002Z' fill='black' fillRule='evenodd' data-sentry-element="path" data-sentry-source-file="Calculator.tsx" />
        </svg>
      </div>
      <Text type='h4' data-sentry-element="Text" data-sentry-source-file="Calculator.tsx">Розрахувати вартість</Text>
      <div className={styles['select-wrapper']}>
        <Select onValueChange={region => setState(prev => ({
        ...prev,
        district: null,
        region
      }))} options={((regions.data?.data as RegionsData)?.regions ?? []).map(region => ({
        label: region,
        value: region
      }))} placeholder='Область' value={state.region ?? ''} data-sentry-element="Select" data-sentry-source-file="Calculator.tsx" />
        <Select disabled={!state.region} disabledOptions={districts.isLoading} onValueChange={district => setState(prev => ({
        ...prev,
        district
      }))} options={districts.isLoading ? loadingDataSelect : ((districts.data?.data as DistrictsData)?.districts ?? []).map(district => ({
        label: district,
        value: district
      }))} placeholder='Район' value={state.district ?? ''} data-sentry-element="Select" data-sentry-source-file="Calculator.tsx" />
        <Input isFilled={!!state.area} onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
        const value = formatNumberValue(e.target.value, 12);
        if (/^\d*\.?\d{0,4}$/.test(value)) {
          setState(prev => ({
            ...prev,
            area: value
          }));
        }
      }} placeholder='Площа (га)' type='number' value={state.area!} data-sentry-element="Input" data-sentry-source-file="Calculator.tsx" />
        {state.area !== null && Number(state.area) < 0.0001 && <Text color='red' type='p4'>
            Мінімальна площа 0.0001 га
          </Text>}
        {Number(state.area) > 9999.9999 && <Text color='red' type='p4'>
            Максимальна площа 9999.9999 га
          </Text>}
        {errorMessage && <Text color='red' type='p4'>
            {errorMessage.message}
          </Text>}
        <Button bordered className={styles['btn-calc']} disabled={!allFieldsFilled || Number(state.area) > 9999.9999 || Number(state.area) < 0.0001} onClick={handleCalculation} data-sentry-element="Button" data-sentry-source-file="Calculator.tsx">
          Розрахувати вартість
        </Button>
        {calculateResult.isPending && <Loader center />}
      </div>

      {calculateResult.data?.data && <div className={styles['result-wrapper']}>
          <div className={styles['title-wrapper']}>
            <Text type='p4'>Орієнтовний ціновий діапазон</Text>
            <TooltipComponent className={styles['isGrayBg']} classNameArrow={styles['isArrowGray']} side='bottom'>
              <Text type='p4'>
                Конкретна ціна залежить від точного розташування ділянки, доступу до зрошення та
                інфраструктури і умов діючого договору оренди. Функціонал більш точного розрахунку
                цін за кадастровим номером буде доступний згодом
              </Text>
            </TooltipComponent>
          </div>
          <div className={styles['price-wrapper']}>
            <Text type='h4'>від {beautifyNumber(calculateResult.data.data.minPrice)} грн</Text>
            <Text align='end' type='h4'>
              до {beautifyNumber(calculateResult.data.data.maxPrice)} грн
            </Text>
          </div>
          <Text align='center' className={styles['result-description']} type='p4'>
            Розмістіть оголошення та почніть отримувати пропозиції від потенційних покупців
          </Text>
          <Link className={styles['btn-wrapper']} href={isAuth ? '/offer/create' : '/auth/sign-in'} onClick={() => setOpenCalculator(false)}>
            <Button asDiv>Розмістити оголошення</Button>
          </Link>
        </div>}
    </div>;
};